import { tokenManager } from './token';

export type BackofficeAppMode = 'forms' | 'documents';

export type OpenBackofficeAppOptions = {
    /** Called when localStorage has no refresh token (popup is not opened). */
    onMissingRefreshToken?: () => void;
};

/** Same query name as finger + API body `refresh_token` (backoffice RefreshTokenPage uses path `/c/:refreshToken`). */
export const buildBackofficeAppUrl = (baseMcUrl: string, mode: BackofficeAppMode, refreshToken?: string): string => {
    const separator = baseMcUrl.includes('?') ? '&' : '?';
    const modeParam = mode === 'forms' ? 'backoffice_mode=true' : 'backoffice_documents=true';
    let url = `${baseMcUrl}${separator}${modeParam}`;
    if (refreshToken) {
        url += `&refresh_token=${encodeURIComponent(refreshToken)}`;
    }
    return url;
};

/**
 * Opens finger with backoffice_mode / backoffice_documents + refresh_token en query (como sesión backoffice).
 * postMessage sigue como refuerzo si la URL se trunca.
 */
export const openBackofficeApp = (
    baseMcUrl: string,
    mode: BackofficeAppMode,
    options?: OpenBackofficeAppOptions
): void => {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
        options?.onMissingRefreshToken?.();
        return;
    }

    const url = buildBackofficeAppUrl(baseMcUrl, mode, refreshToken);
    const popup = window.open(url, '_blank', 'width=411px,height=731px');

    if (!popup) return;

    const sendToken = () => {
        try {
            popup.postMessage(
                {
                    type: 'BACKOFFICE_REFRESH_TOKEN',
                    refreshToken,
                },
                '*'
            );
        } catch (e) {
            console.warn('Failed to send refresh token to finger app', e);
        }
    };

    // Finger mounts listener after slow first paint; it posts BACKOFFICE_APP_READY when ready
    const onFingerReady = (event: MessageEvent) => {
        if (event.data?.type !== 'BACKOFFICE_APP_READY') return;
        if (event.source !== popup) return;
        sendToken();
    };
    window.addEventListener('message', onFingerReady);

    const fallbackDelaysMs = [0, 400, 1200, 2500, 5000, 10000, 20000];
    const timeoutIds = fallbackDelaysMs.map((ms) => setTimeout(sendToken, ms));

    const cleanupMs = 90000;
    window.setTimeout(() => {
        window.removeEventListener('message', onFingerReady);
        timeoutIds.forEach((id) => clearTimeout(id));
    }, cleanupMs);
};
